import React, { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Sparkles, Navigation2, ArrowUpDown, Filter, X,
} from "lucide-react";
import { DASHBOARD, AI } from "../constants/testIds";
import { calculateQuickScore } from "../utils/quickScore";
import { CATEGORY_COLORS, STATUS_LIST } from "../constants/propertyOptions";
import { formatMoney } from "../utils/currency";
import NavigationDialog from "./NavigationDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SORT_OPTIONS = [
  { key: "created_desc", label: "En yeni eklenen" },
  { key: "created_asc", label: "En eski eklenen" },
  { key: "visited_desc", label: "Son gezilen" },
  { key: "updated_desc", label: "Son güncellenen" },
  { key: "price_asc", label: "Fiyat: düşükten yükseğe" },
  { key: "price_desc", label: "Fiyat: yüksekten düşüğe" },
  { key: "score_desc", label: "AI puanı: yüksekten düşüğe" },
  { key: "score_asc", label: "AI puanı: düşükten yükseğe" },
  { key: "personal_desc", label: "Kişisel puanım" },
  { key: "ppm2_asc", label: "Net m² fiyatı" },
  { key: "rent_yield_desc", label: "Kira getirisi" },
  { key: "title_asc", label: "Başlık A–Z" },
];

const LS_SORT = "emlak-list-sort";
const LS_FILTERS = "emlak-list-filters";

const PropertyList = ({
  properties, onPropertyClick,
  selectedForAnalysis, setSelectedForAnalysis,
  onOpenAIPanel, gridView = false,
}) => {
  const [sort, setSort] = useState(() => localStorage.getItem(LS_SORT) || "created_desc");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_FILTERS)) || {}; } catch { return {}; }
  });
  const [navProp, setNavProp] = useState(null);

  React.useEffect(() => { localStorage.setItem(LS_SORT, sort); }, [sort]);
  React.useEffect(() => { localStorage.setItem(LS_FILTERS, JSON.stringify(filters)); }, [filters]);

  const filtered = useMemo(() => {
    return properties.filter((p) => {
      if (filters.status && p.status !== filters.status) return false;
      if (filters.city && p.city !== filters.city) return false;
      if (filters.property_type && p.property_type !== filters.property_type) return false;
      const priceForFilter = p.current_price ?? p.price;
      if (filters.price_min && (priceForFilter ?? 0) < Number(filters.price_min)) return false;
      if (filters.price_max && (priceForFilter ?? Infinity) > Number(filters.price_max)) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        // Deep search: title, code, building_name, address, description,
        // location fields, meeting_notes (person + note text)
        const notesText = (p.meeting_notes || []).map(n =>
          [n.person, n.person_type, n.note].filter(Boolean).join(" ")
        ).join(" ");
        const hay = [
          p.title, p.code, p.building_name, p.address, p.description,
          p.location, p.city, p.district, p.neighborhood, p.street,
          notesText,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [properties, filters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const scoreCache = new Map();
    const getScore = (p) => {
      if (!scoreCache.has(p.id)) scoreCache.set(p.id, calculateQuickScore(p).score);
      return scoreCache.get(p.id);
    };
    const getPPM2 = (p) => {
      const price = p.current_price || p.price || 0;
      const area = p.net_area || p.area;
      return area ? price / area : Infinity;
    };
    const getYield = (p) => {
      const price = p.current_price || p.price || 0;
      return p.estimated_rent && price ? (p.estimated_rent * 12) / price : -Infinity;
    };
    switch (sort) {
      case "created_desc": arr.sort((a,b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      case "created_asc": arr.sort((a,b) => (a.created_at || "").localeCompare(b.created_at || "")); break;
      case "visited_desc": arr.sort((a,b) => (b.location_captured_at || b.created_at || "").localeCompare(a.location_captured_at || a.created_at || "")); break;
      case "updated_desc": arr.sort((a,b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "")); break;
      case "price_asc": arr.sort((a,b) => (a.current_price??a.price??Infinity)-(b.current_price??b.price??Infinity)); break;
      case "price_desc": arr.sort((a,b) => (b.current_price??b.price??-Infinity)-(a.current_price??a.price??-Infinity)); break;
      case "score_desc": arr.sort((a,b) => getScore(b) - getScore(a)); break;
      case "score_asc": arr.sort((a,b) => getScore(a) - getScore(b)); break;
      case "personal_desc": arr.sort((a,b) => (b.personal_like||0) - (a.personal_like||0)); break;
      case "ppm2_asc": arr.sort((a,b) => getPPM2(a) - getPPM2(b)); break;
      case "rent_yield_desc": arr.sort((a,b) => getYield(b) - getYield(a)); break;
      case "title_asc": arr.sort((a,b) => (a.title||"").localeCompare(b.title||"", "tr")); break;
      default: break;
    }
    return arr;
  }, [filtered, sort]);

  const toggle = (id) => {
    if (selectedForAnalysis.includes(id)) setSelectedForAnalysis(selectedForAnalysis.filter(x => x !== id));
    else setSelectedForAnalysis([...selectedForAnalysis, id]);
  };

  const cityOptions = useMemo(() => [...new Set(properties.map(p => p.city).filter(Boolean))], [properties]);
  const typeOptions = useMemo(() => [...new Set(properties.map(p => p.property_type).filter(Boolean))], [properties]);

  return (
    <div
      data-testid={DASHBOARD.propertyList}
      className={gridView ? "w-full" : "h-full flex flex-col min-h-0"}
    >
      {/* Header */}
      <div
        className={
          (gridView
            ? "p-3 border-b border-[#E4E4E7] dark:border-[#27272A] bg-white/70 dark:bg-[#18181B]/70 backdrop-blur-xl space-y-2"
            : "p-3 border-b border-[#E4E4E7] dark:border-[#27272A] bg-white/70 dark:bg-[#18181B]/70 backdrop-blur-xl sticky top-0 z-10 space-y-2")
        }
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">
            İlanlar <span className="text-xs text-[#52525B] dark:text-[#A1A1AA] ml-1">({sorted.length})</span>
          </h2>
          <div className="flex gap-1">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-menu-button" className="h-8 w-auto border-[#E4E4E7] dark:border-[#27272A] px-2 gap-1 text-xs" aria-label="Sırala">
                <ArrowUpDown className="w-3.5 h-3.5" /> Sırala
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(o => <SelectItem key={o.key} value={o.key} className="text-xs">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="filter-toggle-button" variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="h-8 border-[#E4E4E7] dark:border-[#27272A]">
              <Filter className="w-3.5 h-3.5 mr-1" />Filtrele
            </Button>
          </div>
        </div>

        {/* Search box — always visible */}
        <div className="relative">
          <Input
            data-testid="list-search-input"
            value={filters.q || ""}
            onChange={(e) => setFilters({...filters, q: e.target.value})}
            placeholder="Başlık, kod, adres, açıklama, not veya kişi ara..."
            className="h-9 pl-8 text-xs bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
          />
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B] dark:text-[#A1A1AA] pointer-events-none">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#E4E4E7] dark:border-[#27272A]">
            <Select value={filters.status || "__all__"} onValueChange={(v) => setFilters({...filters, status: v === "__all__" ? undefined : v})}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Durum" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Tüm durumlar</SelectItem>{STATUS_LIST.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.city || "__all__"} onValueChange={(v) => setFilters({...filters, city: v === "__all__" ? undefined : v})}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Şehir" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Tüm şehirler</SelectItem>{cityOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.property_type || "__all__"} onValueChange={(v) => setFilters({...filters, property_type: v === "__all__" ? undefined : v})}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Tip" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Tüm tipler</SelectItem>{typeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Min ₺" value={filters.price_min || ""} onChange={(e) => setFilters({...filters, price_min: e.target.value})} className="h-8 text-xs" />
            <Input type="number" placeholder="Max ₺" value={filters.price_max || ""} onChange={(e) => setFilters({...filters, price_max: e.target.value})} className="h-8 text-xs" />
            {Object.keys(filters).length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setFilters({})} className="col-span-2 h-7 text-xs text-red-600">
                <X className="w-3 h-3 mr-1" />Filtreleri Temizle
              </Button>
            )}
          </div>
        )}

        {selectedForAnalysis.length > 0 && (
          <Button data-testid={AI.analyzeButton} size="sm" onClick={onOpenAIPanel} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white h-8">
            <Sparkles className="w-3 h-3 mr-1" />{selectedForAnalysis.length} Gayrimenkulü Analiz Et
          </Button>
        )}
      </div>

      {/* List */}
      {sorted.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6 text-sm text-[#52525B] dark:text-[#A1A1AA]">
          Sonuç yok
        </div>
      ) : (
        <div
          className={
            gridView
              ? "p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 items-start"
              : "flex-1 min-h-0 overflow-y-auto p-3 space-y-2.5"
          }
        >
          {sorted.map((p) => {
            const rawPrice = p.current_price ?? p.price ?? null;
            const price = (typeof rawPrice === "number" && rawPrice > 0) ? rawPrice : null;
            const currency = p.currency || "TRY";
            const currencyOther = p.currency_other || "";
            const rawArea = p.net_area || p.area;
            const area = (typeof rawArea === "number" && rawArea > 0) ? rawArea : null;
            const ppm2 = area && price ? price / area : null;
            const catColor = CATEGORY_COLORS[p.category] || "#9CA3AF";
            const scoreObj = calculateQuickScore(p);
            const photos = p.photos || [];
            const mainPhoto = photos.find(x => x.is_main) || photos[0];
            const imgUrl = mainPhoto ? `${API_URL}/api/photos/${mainPhoto.id}` : p.image_url;
            return (
              <div key={p.id} data-testid="property-card" className="self-start bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg overflow-hidden hover:-translate-y-0.5 transition-transform duration-150 shadow-sm hover:shadow-md">
                {imgUrl && (
                  <div
                    className="h-32 w-full overflow-hidden cursor-pointer bg-[#F4F4F5] dark:bg-[#27272A] relative"
                    onClick={() => onPropertyClick(p)}
                  >
                    <img
                      src={imgUrl}
                      alt=""
                      aria-hidden="true"
                      className="w-full h-full object-cover block"
                      loading="lazy"
                      onError={(e) => {
                        // Prevent broken-image icon + long alt text from
                        // blowing out card height. Replace with a branded
                        // placeholder that fills the fixed slot.
                        const img = e.currentTarget;
                        img.style.display = "none";
                        const parent = img.parentElement;
                        if (parent && !parent.querySelector("[data-fallback='1']")) {
                          const ph = document.createElement("div");
                          ph.setAttribute("data-fallback", "1");
                          ph.className =
                            "absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.2em] font-bold text-[#A1A1AA] dark:text-[#52525B] select-none";
                          ph.textContent = "Görsel yok";
                          parent.appendChild(ph);
                        }
                      }}
                    />
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onPropertyClick(p)}>
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: catColor }} />
                        {p.code && <span className="text-[9px] uppercase tracking-wider font-bold text-[#52525B] dark:text-[#A1A1AA]">{p.code}</span>}
                        <span className="text-[9px] uppercase tracking-wider text-[#52525B] dark:text-[#A1A1AA]">{p.status || p.category}</span>
                      </div>
                      <h3 className="font-bold text-sm text-[#0A0A0A] dark:text-[#FAFAFA] line-clamp-1">{p.title}</h3>
                      <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] line-clamp-1">
                        {p.location || [p.district, p.city].filter(Boolean).join(", ") || "—"}
                      </p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-base font-bold text-[#F59E0B]">
                          {price != null ? formatMoney(price, currency, currencyOther) : "Fiyat girilmedi"}
                        </span>
                        {ppm2 && <span className="text-[10px] text-[#52525B] dark:text-[#A1A1AA]">{formatMoney(Math.round(ppm2), currency, currencyOther)}/m²</span>}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px]">
                        {scoreObj.completeness >= 25 ? (
                          <span className="px-1.5 py-0.5 rounded font-bold text-white" style={{ backgroundColor: scoreObj.score >= 70 ? "#10B981" : scoreObj.score >= 50 ? "#3B82F6" : scoreObj.score >= 35 ? "#F59E0B" : "#EF4444" }}>
                            AI {scoreObj.score}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded font-semibold border border-[#E4E4E7] dark:border-[#27272A] text-[#52525B] dark:text-[#A1A1AA]">
                            Henüz puanlanmadı
                          </span>
                        )}
                        <span className="text-[#52525B] dark:text-[#A1A1AA]">Veri %{scoreObj.completeness}</span>
                        {p.personal_like && <span className="text-[#F59E0B] font-semibold">★ {p.personal_like}/10</span>}
                        {area && <span className="text-[#52525B] dark:text-[#A1A1AA]">{area}m²</span>}
                      </div>
                    </div>
                    <Checkbox
                      data-testid={AI.selectPropertyCheckbox}
                      checked={selectedForAnalysis.includes(p.id)}
                      onCheckedChange={() => toggle(p.id)}
                      className="border-[#E4E4E7] dark:border-[#27272A] mt-1 flex-shrink-0"
                    />
                  </div>
                  {p.lat && p.lng && (
                    <button
                      data-testid="card-navigation-button"
                      onClick={(e) => { e.stopPropagation(); setNavProp(p); }}
                      className="mt-2 w-full flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded transition-colors"
                    >
                      <Navigation2 className="w-3 h-3" />Yol Tarifi
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {navProp && navProp.lat && navProp.lng && (
        <NavigationDialog
          open={!!navProp}
          onClose={() => setNavProp(null)}
          lat={navProp.lat}
          lng={navProp.lng}
          title={navProp.title}
        />
      )}
    </div>
  );
};

export default PropertyList;
